import { computed, inject, Injectable, Signal, signal } from '@angular/core';
import { L10nTranslationService } from 'angular-l10n';
import { LogHelper } from '../../logging/log-helper';
import { DashboardContext } from '../state/dashboard-context';
import { DASHBOARD_CONFIG, DASHBOARD_VIEW_STORE } from '../dashboard-grid.tokens';
import { DashboardLayouts } from '../layout/dashboard-layouts';
import { DashboardLayout } from '../layout/dashboard-layout';
import {
  DashboardSavedViews,
  DashboardView,
  EMPTY_SAVED_VIEWS,
  MAX_SAVED_VIEWS,
  suggestViewName,
  viewNameMessage,
  viewNameProblem,
} from './dashboard-view';
import { uniqueId } from '../../model/utility';
import { parseDashboardViews } from './dashboard-views-parse';

/** Why a view change was refused before reaching the store */
export type DashboardViewRejection =
  /** Another change is being written */
  | 'busy'
  /** The saved views could not be read, so writing would replace them */
  | 'read-only'
  | 'invalid-name'
  | 'limit'
  | 'missing';

export type DashboardViewOutcome =
  /** The view is shown; `remembered` is false when the store could not keep the choice for next time */
  | { kind: 'shown'; name: string; remembered: boolean }
  | { kind: 'saved'; view: DashboardView }
  | { kind: 'deleted'; name: string }
  | { kind: 'reset' }
  /** The store failed, or the change was refused; nothing changed */
  | { kind: 'save-failed'; name: string }
  | { kind: 'delete-failed'; name: string }
  | { kind: 'reset-failed' }
  | { kind: 'rejected'; reason: DashboardViewRejection };

const READ_ONLY: DashboardViewOutcome = { kind: 'rejected', reason: 'read-only' };

interface ViewsState {
  saved: DashboardSavedViews;
  defaultLayout: DashboardLayout;
}

/**
 * The user's saved views of one dashboard and the built-in Default view. Filters reset whenever the shown view's
 * defaults change. Every change is applied to the latest stored views, so views saved elsewhere, e.g. in another tab,
 * are kept.
 */
@Injectable()
export class DashboardViews {
  private readonly config = inject(DASHBOARD_CONFIG);
  private readonly store = inject(DASHBOARD_VIEW_STORE);
  private readonly layouts = inject(DashboardLayouts);
  private readonly context = inject(DashboardContext);
  private readonly translation = inject(L10nTranslationService);

  private readonly state = signal<ViewsState | null>(null);
  private readonly isBusy = signal(false);
  private readonly isReadOnly = signal(true);

  /** The store is being written */
  readonly busy: Signal<boolean> = this.isBusy.asReadonly();
  /** Until loaded, and when the saved views can't be read, so they aren't overwritten */
  readonly readOnly: Signal<boolean> = this.isReadOnly.asReadonly();
  readonly views: Signal<readonly DashboardView[]> = computed(() => this.state()?.saved.views ?? []);
  /** The saved view being shown; null for the Default view */
  readonly activeView: Signal<DashboardView | null> = computed(() => {
    const saved = this.state()?.saved;
    return saved?.views.find((view) => view.id === saved.activeViewId) ?? null;
  });
  readonly atLimit = computed(() => this.views().length >= MAX_SAVED_VIEWS);
  /** The built-in layout; null until loaded */
  readonly defaultLayout: Signal<DashboardLayout | null> = computed(() => this.state()?.defaultLayout ?? null);
  /** Null until loaded */
  readonly shownLayout: Signal<DashboardLayout | null> = computed(
    () => this.activeView()?.layout ?? this.defaultLayout(),
  );

  get defaultViewName(): string {
    return this.translation.translate('Dashboard.Views.Default');
  }

  /** Never rejects: unreadable saved views make the views read-only, an unusable default gives an empty layout. */
  async load(): Promise<void> {
    const warnings: string[] = [];
    const saved = await this.readSaved(warnings);
    this.isReadOnly.set(saved === null);
    const defaultLayout = this.parseDefault(warnings);

    this.logWarnings(warnings);
    this.show({ saved: saved ?? EMPTY_SAVED_VIEWS, defaultLayout }, true);
  }

  /** Shows the view at once, then remembers it; null when it is already shown */
  async select(id: string | null): Promise<DashboardViewOutcome | null> {
    const state = this.state();
    if (!state || state.saved.activeViewId === id) {
      return null;
    }

    const rejection = this.rejection() ?? (this.findView(id) === undefined ? 'missing' : null);
    if (rejection) {
      return { kind: 'rejected', reason: rejection };
    }

    this.show({ ...state, saved: { ...state.saved, activeViewId: id } }, true);
    const name = this.activeView()?.name ?? this.defaultViewName;
    const remembered = await this.withLatest((latest) => this.write(latest.saved), false);

    return { kind: 'shown', name, remembered };
  }

  /** Why `name` cannot name a view; the view `replacing` may keep its own name */
  nameError(name: string, replacing: string | null): string | null {
    const problem = viewNameProblem(name, this.takenNames(replacing));
    return problem && viewNameMessage(problem, this.translation);
  }

  suggestName(): string {
    return suggestViewName(this.translation.translate('Dashboard.Views.Suggested'), this.takenNames(null));
  }

  async save(layout: DashboardLayout, name: string, replacing: string | null): Promise<DashboardViewOutcome> {
    const rejection = this.rejection() ?? this.saveRejection(name, replacing);
    if (rejection) {
      return { kind: 'rejected', reason: rejection };
    }

    return this.withLatest((latest) => this.saveTo(latest, layout, name, replacing), READ_ONLY);
  }

  async delete(id: string): Promise<DashboardViewOutcome> {
    const rejection = this.rejection();
    const deleted = this.findView(id);
    if (rejection || !deleted) {
      return { kind: 'rejected', reason: rejection ?? 'missing' };
    }

    return this.withLatest(async (latest) => {
      const saved: DashboardSavedViews = {
        ...latest.saved,
        activeViewId: null,
        views: latest.saved.views.filter((view) => view.id !== id),
      };

      if (!(await this.write(saved))) {
        return { kind: 'delete-failed', name: deleted.name };
      }

      this.show({ ...latest, saved }, true);
      return { kind: 'deleted', name: deleted.name };
    }, READ_ONLY);
  }

  /** Null when the saved views can be read */
  async reset(): Promise<DashboardViewOutcome | null> {
    const state = this.state();
    if (!state || !this.isReadOnly()) {
      return null;
    }

    if (this.isBusy()) {
      return { kind: 'rejected', reason: 'busy' };
    }

    this.isBusy.set(true);
    const written = await this.write(EMPTY_SAVED_VIEWS);
    this.isBusy.set(false);
    if (!written) {
      return { kind: 'reset-failed' };
    }

    this.isReadOnly.set(false);
    this.show({ ...state, saved: EMPTY_SAVED_VIEWS }, false);

    return { kind: 'reset' };
  }

  /** The view `id`, null for the Default view, or undefined when there is no such view */
  private findView(id: string | null): DashboardView | null | undefined {
    return id === null ? null : this.views().find((view) => view.id === id);
  }

  private rejection(): DashboardViewRejection | null {
    if (this.isReadOnly()) {
      return 'read-only';
    }

    return this.isBusy() ? 'busy' : null;
  }

  private async saveTo(
    latest: ViewsState,
    layout: DashboardLayout,
    name: string,
    replacing: string | null,
  ): Promise<DashboardViewOutcome> {
    const rejection = this.saveRejection(name, replacing);
    if (rejection) {
      return { kind: 'rejected', reason: rejection };
    }

    const view: DashboardView = { id: replacing ?? uniqueId('view', this.views()), name: name.trim(), layout };
    const views = replacing
      ? latest.saved.views.map((candidate) => (candidate.id === replacing ? view : candidate))
      : [...latest.saved.views, view];
    const saved: DashboardSavedViews = { ...latest.saved, activeViewId: view.id, views };

    if (!(await this.write(saved))) {
      return { kind: 'save-failed', name: view.name };
    }

    const defaultsChanged = !sameFilterDefaults(this.shownLayout()?.filterDefaults, layout.filterDefaults);
    this.show({ ...latest, saved }, defaultsChanged);

    return { kind: 'saved', view };
  }

  private saveRejection(name: string, replacing: string | null): DashboardViewRejection | null {
    const replaced = this.findView(replacing);
    if (replaced === undefined) {
      return 'missing';
    }

    if (this.nameError(name, replacing)) {
      return 'invalid-name';
    }

    return !replaced && this.atLimit() ? 'limit' : null;
  }

  /** A saved view may keep a stored name that matches the Default view's name in another language */
  private takenNames(replacing: string | null): string[] {
    const replaced = this.findView(replacing);
    const saved = this.views().filter((view) => view !== replaced);
    const keepsDefaultName = !!replaced && viewNameProblem(replaced.name, [this.defaultViewName]) === 'taken';
    return [...(keepsDefaultName ? [] : [this.defaultViewName]), ...saved.map((view) => view.name)];
  }

  private show(next: ViewsState, resetFilters: boolean): void {
    this.state.set(next);
    if (resetFilters) {
      this.context.applyDefaults(this.shownLayout()?.filterDefaults);
    }
  }

  /** Busy while it runs `change` on the latest stored views; `unreadable` when they can no longer be read */
  private async withLatest<T>(change: (latest: ViewsState) => Promise<T>, unreadable: T): Promise<T> {
    this.isBusy.set(true);
    try {
      const latest = await this.refresh();
      return latest ? await change(latest) : unreadable;
    } finally {
      this.isBusy.set(false);
    }
  }

  /** Takes in the stored views, keeping the shown view while it exists; null, and read-only, when they can't be read */
  private async refresh(): Promise<ViewsState | null> {
    const warnings: string[] = [];
    const stored = await this.readSaved(warnings);
    this.logWarnings(warnings);
    const state = this.state();
    if (!stored || !state) {
      this.isReadOnly.set(true);
      return null;
    }

    const shownId = state.saved.activeViewId;
    const activeViewId = stored.views.some((view) => view.id === shownId) ? shownId : null;
    const shownDefaults = this.shownLayout()?.filterDefaults;
    const latest: ViewsState = { ...state, saved: { ...stored, activeViewId } };
    this.state.set(latest);
    if (!sameFilterDefaults(shownDefaults, this.shownLayout()?.filterDefaults)) {
      this.context.applyDefaults(this.shownLayout()?.filterDefaults);
    }

    return latest;
  }

  /** True once the store has the document */
  private async write(saved: DashboardSavedViews): Promise<boolean> {
    try {
      await this.store.save(this.config.id, saved);
      return true;
    } catch (error) {
      LogHelper.error('DashboardViews', `Dashboard "${this.config.id}" views could not be saved`, error);
      return false;
    }
  }

  private logWarnings(warnings: string[]): void {
    if (warnings.length > 0) {
      LogHelper.warn('DashboardViews', `Dashboard "${this.config.id}" layout warnings:`, warnings);
    }
  }

  /** The saved views; null when they exist but cannot be read */
  private async readSaved(warnings: string[]): Promise<DashboardSavedViews | null> {
    let raw: unknown;

    try {
      raw = await this.store.load(this.config.id);
    } catch (error) {
      LogHelper.error('DashboardViews', `Dashboard "${this.config.id}" views could not be loaded`, error);
      return null;
    }

    // null and undefined mean "nothing saved", not a malformed document
    if (raw === null || raw === undefined) {
      return EMPTY_SAVED_VIEWS;
    }

    const result = parseDashboardViews(raw, this.layouts);
    warnings.push(...result.warnings.map((warning) => `[saved] ${warning}`));
    if (!result.views) {
      LogHelper.error('DashboardViews', `Dashboard "${this.config.id}" has saved views it cannot read`);
    }

    return result.views;
  }

  private parseDefault(warnings: string[]): DashboardLayout {
    const { layout, warnings: layoutWarnings, unusable } = this.layouts.parseDefault(this.config.defaultLayout);
    if (unusable) {
      LogHelper.error('DashboardViews', `Dashboard "${this.config.id}" has an unusable built-in layout`);
    }

    warnings.push(...layoutWarnings.map((warning) => `[default] ${warning}`));
    return layout;
  }
}

function sameFilterDefaults(a: Record<string, unknown> = {}, b: Record<string, unknown> = {}): boolean {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  return [...keys].every((key) => Object.is(a[key], b[key]));
}
